import { App } from 'vue';

import Exception from '@/common/models/Exception';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { isObjectEmpty } from '@/globals';
// Evaluators
import AfterEvaluator from '@/validator/evaluators/AfterEvaluator';
import AfterOrEqualEvaluator from '@/validator/evaluators/AfterOrEqualEvaluator';
import BeforeEvaluator from '@/validator/evaluators/BeforeEvaluator';
import BeforeOrEqualEvaluator from '@/validator/evaluators/BeforeOrEqualEvaluator';
import BooleanEvaluator from '@/validator/evaluators/BooleanEvaluator';
import DateTimeEvaluator from '@/validator/evaluators/DateTimeEvaluator';
import DomainEvaluator from '@/validator/evaluators/DomainEvaluator';
import EmailEvaluator from '@/validator/evaluators/EmailEvaluator';
import GreaterThanEvaluator from '@/validator/evaluators/GreaterThanEvaluator';
import InEvaluator from '@/validator/evaluators/InEvaluator';
import IPEvaluator from '@/validator/evaluators/IPEvaluator';
import LessThanEvaluator from '@/validator/evaluators/LessThanEvaluator';
import MaxEvaluator from '@/validator/evaluators/MaxEvaluator';
import MaxValueEvaluator from '@/validator/evaluators/MaxValueEvaluator';
import MinEvaluator from '@/validator/evaluators/MinEvaluator';
import MinValueEvaluator from '@/validator/evaluators/MinValueEvaluator';
import NotEmptyEvaluator from '@/validator/evaluators/NotEmptyEvaluator';
import NotEmptyOrWhitespaceEvaluator from '@/validator/evaluators/NotEmptyOrWhitespaceEvaluator';
import NotInEvaluator from '@/validator/evaluators/NotInEvaluator';
import NotInUseEvaluator from '@/validator/evaluators/NotInUseEvaluator';
import NumericEvaluator from '@/validator/evaluators/NumericEvaluator';
import PasswordEvaluator from '@/validator/evaluators/PasswordEvaluator';
import RequiredEvaluator from '@/validator/evaluators/RequiredEvaluator';
import RequiredOrEvaluator from '@/validator/evaluators/RequiredOrEvaluator';
import StringEqualsEvaluator from '@/validator/evaluators/StringEqualsEvaluator';
import URLEvaluator from '@/validator/evaluators/URLEvaluator';
import URLWithMacrosEvaluator from '@/validator/evaluators/URLWithMacrosEvaluator';
import UUIDEvaluator from '@/validator/evaluators/UUIDEvaluator';
import messagesDE from '@/validator/i18n/validationMessages.de';
import messagesEN from '@/validator/i18n/validationMessages.en';
import messagesES from '@/validator/i18n/validationMessages.es';
import messagesFR from '@/validator/i18n/validationMessages.fr';
import messagesJA from '@/validator/i18n/validationMessages.ja';
import Validatable from '@/validator/Validatable';
import ValidatorMessageBag from '@/validator/ValidatorMessageBag';
import i18n from '@/vueI18n';

let LOCALE = 'en';
const DICTIONARY = {
  en: messagesEN,
  'en-AU': messagesEN,
  'en-CA': messagesEN,
  'en-GB': messagesEN,
  'en-US': messagesEN,
  fr: messagesFR,
  'fr-CA': messagesFR,
  'fr-FR': messagesFR,
  de: messagesDE,
  'de-DE': messagesDE,
  ja: messagesJA,
  'ja-JP': messagesJA,
  es: messagesES,
  'es-MX': messagesES,
};

export const EVALUATORS = {
  required: RequiredEvaluator,
  required_or: RequiredOrEvaluator,
  numeric: NumericEvaluator,
  boolean: BooleanEvaluator,
  max_value: MaxValueEvaluator,
  min_value: MinValueEvaluator,
  max: MaxEvaluator,
  min: MinEvaluator,
  after: AfterEvaluator,
  before: BeforeEvaluator,
  after_or_equal: AfterOrEqualEvaluator,
  before_or_equal: BeforeOrEqualEvaluator,
  domain: DomainEvaluator,
  email: EmailEvaluator,
  ip: IPEvaluator,
  not_empty: NotEmptyEvaluator,
  not_empty_or_whitespace: NotEmptyOrWhitespaceEvaluator,
  in: InEvaluator,
  not_in: NotInEvaluator,
  not_in_use: NotInUseEvaluator,
  string_equals: StringEqualsEvaluator,
  uuid: UUIDEvaluator,
  greater_than: GreaterThanEvaluator,
  less_than: LessThanEvaluator,
  password: PasswordEvaluator,
  url: URLEvaluator,
  url_with_macros: URLWithMacrosEvaluator,
  date_time: DateTimeEvaluator,
};

class Validator {
  app: App;
  model;
  modelToViewMappings;
  evaluators: typeof EVALUATORS;
  messageBag: ValidatorMessageBag;
  static localizedStrings: Record<string, string> = {};
  action: string;

  constructor(app) {
    this.app = app;
    this.model = null;
    this.modelToViewMappings = null;
    this.evaluators = EVALUATORS;
    this.messageBag = new ValidatorMessageBag();
  }

  static {
    GlobalEventBus.$on(GlobalEvents.languageChanged, () => {
      Validator.localizedStrings = {};
    });
  }

  init(validatableModel, action = 'create', modelToViewMappings = {}) {
    if (validatableModel instanceof Validatable) {
      this.model = validatableModel;
      this.action = action;
      this.modelToViewMappings = modelToViewMappings; // TODO: create mappings for overrides
    } else {
      throw new Exception('Error: Expected model that extends `Validatable`');
    }
  }

  /**
   * This is basically a re-call of init() for the simple purpose of having
   * something named appropriately
   * @param validatableModel
   * @param action
   * @param view
   * @param modelToViewMappings
   */
  rebindModel(validatableModel, action = 'create', modelToViewMappings = {}) {
    this.init(validatableModel, action, modelToViewMappings);
  }

  getCreateRules(validatableModel) {
    return this.model.constructor.validatorCreateRules(validatableModel);
  }

  getUpdateRules(validatableModel) {
    return this.model.constructor.validatorUpdateRules(validatableModel);
  }

  /**
   * @param {*} validatableModel
   * @returns {Record<string, string>}
   */
  getRules(validatableModel = this.model) {
    if (this.action === 'create') {
      return this.getCreateRules(validatableModel);
    }
    if (this.action === 'update') {
      return this.getUpdateRules(validatableModel);
    }
    return {};
  }

  validateAll(validatableModel, optionalPayload = {}, doNotClearMessageBag = false) {
    if (!this.action) {
      // Warn the developer so that they fix the validator's initialization.
      // e.g.: this.$validator.init(<ENTITY-MODEL>);
      console.warn('Validator was never initialized!');
    }

    if (!doNotClearMessageBag) {
      this.messageBag.clear();
    }

    let allValid = true;

    // Process pre-evaluated items first.
    Object.keys(optionalPayload).forEach((payloadKey) => {
      if (optionalPayload[payloadKey].preEvaluated === true) {
        this.messageBag.addByType(payloadKey, 'error', optionalPayload[payloadKey].message);
        delete optionalPayload[payloadKey];
        allValid = false;
      }
    });

    if (validatableModel instanceof Validatable) {
      this.model = validatableModel;
    }
    const fields = this.getRules(validatableModel);

    // Merge object and optional payload rules
    Object.keys(optionalPayload).forEach((payloadKey) => {
      if (payloadKey in fields) {
        fields[payloadKey] = `${fields[payloadKey]}|${optionalPayload[payloadKey].rules}`;
        delete optionalPayload[payloadKey];
      }
    });

    const evaluatableModel = {};
    Object.keys(validatableModel).forEach((key) => {
      evaluatableModel[key] = validatableModel[key];
    });
    Object.keys(optionalPayload).forEach((key) => {
      evaluatableModel[key] = optionalPayload[key];
    });
    Object.keys(fields).forEach((key) => {
      // Iterate the validatable fields
      const isComplexObject = typeof fields[key] !== 'string';
      const formName = isComplexObject ? fields[key].formName : key;
      const ruleString = isComplexObject ? fields[key].rule : fields[key];
      const rulesRaw =
        ruleString !== null && ruleString !== undefined && ruleString.trim() !== '' ? ruleString.split('|') : [];
      rulesRaw.forEach((rule) => {
        // Iterate the evaluators for a particular field
        let name = rule;
        let value = null;
        if (rule.indexOf(':') >= 0) {
          // evaluate rule with value
          const nv = rule.split(':');
          [name] = nv;
          if (nv.length === 2) {
            [, value] = nv;
          } else if (nv.length > 2) {
            // Handle case where mutator contains ':' character (eg. time)
            value = '';
            for (let i = 1; i < nv.length; i += 1) {
              if (i !== 1) {
                value += ':';
              }
              value += nv[i];
            }
          }
        }
        // let found = false;
        if (name in this.evaluators) {
          const Evaluator = this.evaluators[name];
          const evalObj = new Evaluator(
            key,
            Validator.getLocalizedValidatorString(formName),
            evaluatableModel,
            [value],
            this.app,
          );
          const isValid = evalObj.validate();
          allValid = isValid && allValid;
          const message = evalObj.getMessage(Validator.dictionary);
          if (!isValid) {
            window.console.log(`Validation failed for ${name}: ${message.message}`);
          }
          this.messageBag.addByType(formName, message.type, message.message);
        } else {
          window.console.log(`Did not find evaluator for ${name}`);
        }
      });
    });
    if (!isObjectEmpty(optionalPayload)) {
      allValid = this.manualValidateAllInternal(optionalPayload, false) && allValid;
    }

    return allValid ? Promise.resolve() : Promise.reject(new Error('Validation failed :('));
  }

  /**
   * Expects a map of maps
   * eg.
   * {
   *    domName1: {value: actualValue1, rules: 'required|max_value:3'},
   *    domName2: {value: actualValue2, rules: 'required|locale:en|dateFormat:YYYY/MM/DD'},
   * }
   * @param payload
   * @param clearMessages
   */
  manualValidateAll(payload = {}, clearMessages = true) {
    return this.manualValidateAllInternal(payload, clearMessages)
      ? Promise.resolve()
      : Promise.reject(new Error('Validation failed :( See message bag'));
  }

  manualValidateAllInternal(payload = {}, clearMessages = true) {
    let allValid = true;
    if (clearMessages) {
      this.messageBag.clear();
    }
    Object.keys(payload).forEach((domName) => {
      const response = this.manualValidate(
        payload[domName].value,
        payload[domName].rules,
        domName,
        this.payloadToModel(payload),
      );
      allValid = allValid && response.isValid;
      if (!response.isValid) {
        window.console.log(`${domName} is not valid:`);
        window.console.log(response.messageBag);
      }
      this.messageBag.addAllFromMessageBag(response.messageBag);
    });
    return allValid;
  }

  /**
   * Validates a single value against a set of rules provided as a string
   * @param value required param
   * @param rules required param
   * @param domName not necessary in all cases
   * @param model not necessary
   * @returns {{isValid: boolean, messageBag: ValidatorMessageBag}}
   */
  manualValidate(value = null, rules = 'required', domName = 'defaultName', model = {}) {
    let isValid = true;
    const messageBag = new ValidatorMessageBag();
    const rulesRaw = rules !== null && rules !== undefined && rules.trim() !== '' ? rules.split('|') : [];
    rulesRaw.forEach((rule) => {
      // Iterate the evaluators for a particular field
      let name = rule;
      let mutator = null;
      if (rule.indexOf(':') >= 0) {
        // evaluate rule with value
        const nv = rule.split(':');
        [name] = nv;
        if (nv.length === 2) {
          [, mutator] = nv;
        } else if (nv.length > 2) {
          // Handle case where mutator contains ':' character (eg. time)
          mutator = '';
          for (let i = 1; i < nv.length; i += 1) {
            if (i !== 1) {
              mutator += ':';
            }
            mutator += nv[i];
          }
        }
      }
      // let found = false;
      if (name in this.evaluators) {
        const Evaluator = this.evaluators[name];
        model[domName] = value;
        const evalObj = new Evaluator(
          domName,
          i18n.global.te(`validatorNames.${domName}`) ? i18n.global.t(`validatorNames.${domName}`) : domName,
          model,
          [mutator],
          this.app,
        );
        isValid = evalObj.validate() && isValid;
        const message = evalObj.getMessage(Validator.dictionary);
        messageBag.addByType(domName, message.type, message.message);
      } else {
        window.console.log(`Did not find evaluator for ${name}`);
      }
    });
    return {
      isValid,
      messageBag,
    };
  }

  payloadToModel(payload = {}) {
    const model = {};
    Object.keys(payload).forEach((key) => {
      model[key] = payload[key].value;
    });
    return model;
  }

  static combineRules(ruleList = []) {
    let str = '';
    ruleList.forEach((rule) => {
      if (str !== '') {
        str = `${str}|`;
      }
      str = `${str}${rule}`;
    });
    return str;
  }

  static setLocale(language = 'en') {
    LOCALE = language;
  }

  static addDictionary(locale, dictionary) {
    DICTIONARY[locale] = dictionary;
  }

  static get dictionary() {
    return DICTIONARY[LOCALE];
  }

  static get locale() {
    return LOCALE;
  }

  static getLocalizedValidatorString(formName) {
    if (Validator.localizedStrings[formName] == null) {
      Validator.localizedStrings[formName] = i18n.global.te(`validatorNames.${formName}`)
        ? i18n.global.t(`validatorNames.${formName}`)
        : formName;
    }
    return Validator.localizedStrings[formName];
  }
}

export default Validator;
